// Auth0 browser login for the static origin.
//
// Authorization Code with PKCE, which is the only safe flow for a bundle a
// visitor can read: the client holds no secret, and the code exchange is bound
// to a verifier this page generated.
//
// The access token is held in MEMORY ONLY. auth0-spa-js defaults to that, and it
// is set explicitly below so a later default change cannot quietly move tokens
// into localStorage, where any script injected into this page could read one.
// The cost is that a full page reload has no token, which is what
// `useRefreshTokens` covers: a rotating, single-use refresh token in an
// httpOnly-equivalent store mints a new access token without a redirect.
//
// Nothing here is a secret. The domain, client id and audience are public
// identifiers, injected at build time by build.mjs.
//
// The host is the enforcement point, not this module: this bundle is readable by
// anyone who requests it, so nothing it decides is a control. A deployed host
// verifies the token on every route the bundle calls and answers 401 without
// one. If the bundle is served without Auth0 configuration — the local preview,
// which has no tenant to authenticate against — it makes unauthenticated
// requests and the host, in that environment alone, answers them. It does not
// invent a session either way.

import { createAuth0Client } from "@auth0/auth0-spa-js";

// Replaced at build time by build.mjs. Empty when the build was given no
// Auth0 configuration.
const CONFIG = __VD_AUTH0__;

// Auth0 compares redirect URIs EXACTLY, trailing slash included. This must stay
// in step with `callback_urls` in infra/stacks/auth0/main.tf.
const REDIRECT_URI = `${window.location.origin}/`;

let client = null;

/** Whether this build was given an Auth0 tenant to talk to. */
export function isConfigured() {
  return Boolean(CONFIG && CONFIG.domain && CONFIG.clientId && CONFIG.audience);
}

/**
 * Complete any redirect in progress and return the session state.
 *
 * Returns `{ authenticated, configured }`. It never throws for the ordinary
 * "not signed in yet" case: that is a state to render, not an error.
 */
export async function initAuth() {
  if (!isConfigured()) {
    return { configured: false, authenticated: false };
  }

  client = await createAuth0Client({
    domain: CONFIG.domain,
    clientId: CONFIG.clientId,
    authorizationParams: {
      audience: CONFIG.audience,
      redirect_uri: REDIRECT_URI,
      // Asked for explicitly rather than inherited from the SDK default. The
      // host reads the verified email from /userinfo to match an operator's
      // pre-seeded invitation, and without `email` in the granted scopes that
      // document carries no address, so the invitation can never be redeemed
      // and the visitor is told to contact Inglis about something already
      // arranged for them.
      scope: "openid profile email",
    },
    // Without the audience above Auth0 issues an opaque token rather than a
    // JWT, and every API route 401s while login appears to succeed. The
    // resource server also has to allow offline access for this to work.
    useRefreshTokens: true,
    cacheLocation: "memory",
  });

  // Returning from the identity provider. Auth0 puts `code` and `state` on the
  // query string; handleRedirectCallback exchanges them.
  const query = new URLSearchParams(window.location.search);
  if (query.has("code") && query.has("state")) {
    await client.handleRedirectCallback();
    // Drop the code and state from the URL so a reload or a shared link does not
    // replay a consumed authorization code, and keep any other parameters the
    // app uses (?tab=).
    query.delete("code");
    query.delete("state");
    const rest = query.toString();
    window.history.replaceState({}, "", rest ? `/?${rest}` : "/");
  }

  return { configured: true, authenticated: await client.isAuthenticated() };
}

/** Send the browser to Auth0 to sign in. */
export async function login() {
  if (!client) {
    throw new Error("auth is not configured in this build");
  }
  await client.loginWithRedirect();
}

/** Sign out locally and at the tenant, returning to this origin. */
export async function logout() {
  if (!client) {
    return;
  }
  await client.logout({ logoutParams: { returnTo: REDIRECT_URI } });
}

/**
 * The current access token, or null when there is no session.
 *
 * Silently renews through the rotating refresh token when the access token has
 * expired. A failure here is a genuine "no longer signed in", so it resolves to
 * null rather than throwing into a render.
 */
export async function accessToken() {
  if (!client) {
    return null;
  }
  try {
    return await client.getTokenSilently();
  } catch (error) {
    if (error && (error.error === "login_required" || error.error === "consent_required")) {
      return null;
    }
    console.error("token renewal failed", error);
    return null;
  }
}

/** The signed-in user's profile, for display only. Never authority. */
export async function profile() {
  if (!client) {
    return null;
  }
  return (await client.getUser()) || null;
}
